/**
 * Row models and conversions for the two SFTP-push tables:
 *  - Column Mapping  (csv.columns + csv.column_header_mapping)
 *  - Response Status Mapping (csv.response_status_mapping)
 *
 * Each row carries a stable uuid `id` so table edits don't reorder rows. The
 * response-status table mixes `status_groups` (no field selected) and
 * `special_cases` (field + value clauses) into one list.
 */
import { v4 as uuidv4 } from "uuid";
import {
  SftpClause,
  SftpResponseStatusMapping,
  SftpSpecialCase,
  SftpStatusGroup,
} from "../../../../../data-model/nycbsSftpPushConfig";
import {
  clauseFieldName,
  describeSpecialCase,
  isEditableSpecialCase,
  LEGACY_SECOND_STP_VALUES,
} from "./specialCaseRoundTrip";

export interface ColumnMappingRow {
  id: string;
  bqColumn: string;
  finalHeader: string;
}

export interface ResponseStatusRow {
  id: string;
  /** Selected condition field/column name; empty means a response_status status_group row. Legacy compound special cases store the second_stp_status value here instead. */
  secondStp: string;
  /** Values for the selected field, or response_status values when `secondStp` is empty. */
  statuses: string[];
  code: string;
  _comment?: string;
  operator?: "AND" | "OR";
  /**
   * Set when the stored special case uses a clause shape this table cannot
   * represent (multi-value IN, nested groups, or more than one condition
   * field). The row renders read-only and saves back byte-for-byte, so opening
   * the page never rewrites a rule the editor could not fully show.
   */
  raw?: SftpSpecialCase;
  /** Human-readable rendering of `raw`, shown in place of the editable inputs. */
  rawSummary?: string;
}

/**
 * Returns whether a row uses the legacy compound special-case shape where
 * `secondStp` holds a second_stp_status value and `statuses` holds response_status values.
 *
 * @param secondStp - Row field column value
 * @return True when the row should round-trip as a mixed-field special case
 */
function isLegacyCompoundSecondStpRow(secondStp: string): boolean {
  return LEGACY_SECOND_STP_VALUES.has(secondStp.trim());
}

/** Response-status special-case clauses always use a single string value. */
const clauseValueAsString = (clause: SftpClause): string => {
  const value = clause.value;
  if (Array.isArray(value)) {
    return value[0] ?? "";
  }
  return value ?? "";
};

/** Builds Column Mapping rows in `csv.columns` order. */
export function columnsToRows(
  columns: string[],
  mapping: Record<string, string>,
): ColumnMappingRow[] {
  return (columns ?? []).map((col) => ({
    id: uuidv4(),
    bqColumn: col,
    finalHeader: mapping?.[col] ?? "",
  }));
}

/** Rebuilds `{ columns, column_header_mapping }` from Column Mapping rows. */
export function rowsToColumns(rows: ColumnMappingRow[]): {
  columns: string[];
  column_header_mapping: Record<string, string>;
} {
  const columns: string[] = [];
  const column_header_mapping: Record<string, string> = {};
  for (const row of rows) {
    const key = row.bqColumn.trim();
    if (!key) continue;
    columns.push(key);
    column_header_mapping[key] = row.finalHeader;
  }
  return { columns, column_header_mapping };
}

/**
 * Converts legacy mixed-field special cases into the UI row shape.
 *
 * @param clauses - Stored special-case clauses
 * @return Field name and value list for the mapping row
 */
function specialCaseClausesToRow(clauses: SftpClause[]): {
  secondStp: string;
  statuses: string[];
  operator: "AND" | "OR";
} {
  if (clauses.length === 0) {
    return { secondStp: "", statuses: [], operator: "AND" };
  }

  const fields = [...new Set(clauses.map((clause) => clauseFieldName(clause)).filter(Boolean))];
  if (fields.length === 1) {
    return {
      secondStp: fields[0],
      statuses: clauses
        .map((clause) => clauseValueAsString(clause))
        .filter((value) => value !== ""),
      operator: "OR",
    };
  }

  const secondStpClause = clauses.find(
    (clause) => clauseFieldName(clause) === "second_stp_status",
  );
  const responseClauses = clauses.filter(
    (clause) => clauseFieldName(clause) === "response_status",
  );

  if (secondStpClause) {
    return {
      secondStp: clauseValueAsString(secondStpClause),
      statuses: responseClauses
        .map((clause) => clauseValueAsString(clause))
        .filter((value) => value !== ""),
      operator: "AND",
    };
  }

  const primaryField = clauseFieldName(clauses[0]);
  return {
    secondStp: primaryField,
    statuses: clauses
      .filter((clause) => clauseFieldName(clause) === primaryField)
      .map((clause) => clauseValueAsString(clause))
      .filter((value) => value !== ""),
    operator: "OR",
  };
}

/** Builds Response Status rows: special cases first (field set), then groups. */
export function mappingToRows(
  mapping: SftpResponseStatusMapping | undefined,
): ResponseStatusRow[] {
  const specials = (mapping?.special_cases ?? []).map((sc) => {
    const parsed = specialCaseClausesToRow(sc.clauses ?? []);
    return {
      id: uuidv4(),
      secondStp: parsed.secondStp,
      statuses: parsed.statuses,
      code: String(sc.code ?? ""),
      _comment: sc._comment,
      operator: parsed.operator,
      // Clause shapes the row model cannot hold are carried through verbatim
      // instead of being collapsed on the next save.
      ...(isEditableSpecialCase(sc)
        ? {}
        : { raw: sc, rawSummary: describeSpecialCase(sc) }),
    };
  });

  const groups = (mapping?.status_groups ?? []).map((g) => ({
    id: uuidv4(),
    secondStp: "",
    statuses: [...(g.statuses ?? [])],
    code: String(g.code ?? ""),
    _comment: g._comment,
  }));

  return [...specials, ...groups];
}

/** Rebuilds `response_status_mapping` from Response Status rows. */
export function rowsToMapping(
  rows: ResponseStatusRow[],
): SftpResponseStatusMapping {
  const status_groups: SftpStatusGroup[] = [];
  const special_cases: SftpSpecialCase[] = [];

  for (const row of rows) {
    // Read-only advanced rule — save it back exactly as it was stored.
    if (row.raw) {
      special_cases.push(row.raw);
      continue;
    }

    const code = row.code.trim();
    const fieldName = row.secondStp.trim();

    if (fieldName) {
      const trimmedStatuses = row.statuses
        .map((value) => value.trim())
        .filter((value) => value !== "");
      const isLegacyCompound = isLegacyCompoundSecondStpRow(fieldName);

      const clauses: SftpClause[] = isLegacyCompound
        ? [
            { field: "second_stp_status", value: fieldName },
            ...trimmedStatuses.map((value) => ({
              field: "response_status",
              value,
            })),
          ]
        : trimmedStatuses.map((value) => ({ field: fieldName, value }));

      if (clauses.length === 0) continue;

      const special: SftpSpecialCase = {
        operator: isLegacyCompound ? (row.operator ?? "AND") : (row.operator ?? "OR"),
        code,
        clauses,
      };
      if (row._comment) special._comment = row._comment;
      special_cases.push(special);
      continue;
    }

    const group: SftpStatusGroup = {
      code,
      statuses: row.statuses.map((value) => value.trim()).filter((value) => value !== ""),
    };
    if (row._comment) group._comment = row._comment;
    status_groups.push(group);
  }

  return { status_groups, special_cases };
}
